<div class="p-8 min-h-screen bg-gray-50">
  <!-- Header -->
  <div class="mb-6 flex items-center justify-between">
    <div>
      <h1 class="text-3xl font-bold text-gray-900">Vehicles</h1>
      <p class="text-gray-600 mt-1">Manage vehicle records</p>
    </div>
    <button
      (click)="openCreateModal()"
      class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
    >
      <i class="fas fa-plus mr-2"></i>Add Vehicle
    </button>
  </div>

  <!-- Search -->
  <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
    <div class="flex gap-4">
      <div class="flex-1">
        <div class="relative">
          <input
            type="text"
            [(ngModel)]="searchTerm"
            (input)="onSearchChange()"
            placeholder="Search by VIN, make, model, year, or color..."
            class="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
        </div>
      </div>
      <button
        (click)="searchByVIN()"
        [disabled]="searchTerm.trim().length !== 17"
        class="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Search by VIN
      </button>
    </div>
  </div>

  <!-- Error Message -->
  @if (error) {
    <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
      <i class="fas fa-exclamation-circle mr-2"></i>{{ error }}
    </div>
  }

  <!-- Loading State -->
  @if (loading) {
    <div class="text-center py-12">
      <i class="fas fa-spinner fa-spin text-4xl text-primary"></i>
      <p class="mt-4 text-gray-600">Loading vehicles...</p>
    </div>
  }

  <!-- Vehicles Table -->
  @if (!loading && filteredVehicles.length > 0) {
    <div class="bg-white rounded-lg shadow-sm overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VIN</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Make</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Color</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          @for (vehicle of filteredVehicles; track vehicle.id) {
            <tr class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{{ vehicle.vin }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ vehicle.make }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ vehicle.model }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ vehicle.year }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ vehicle.color || 'N/A' }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }

  <!-- Empty State -->
  @if (!loading && filteredVehicles.length === 0) {
    <div class="bg-white rounded-lg shadow-sm p-12 text-center">
      <i class="fas fa-car text-6xl text-gray-300 mb-4"></i>
      <h3 class="text-xl font-semibold text-gray-900 mb-2">No vehicles found</h3>
      <p class="text-gray-600 mb-6">Add a vehicle to get started</p>
      <button
        (click)="openCreateModal()"
        class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
      >
        <i class="fas fa-plus mr-2"></i>Add Vehicle
      </button>
    </div>
  }

  <!-- Create Vehicle Modal -->
  @if (showCreateModal) {
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-2xl font-bold text-gray-900">Add New Vehicle</h2>
        </div>
        <form [formGroup]="createVehicleForm" (ngSubmit)="onCreateVehicle()" class="p-6">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">VIN * (17 characters)</label>
              <input
                type="text"
                formControlName="vin"
                maxlength="17"
                placeholder="1HGBH41JXMN109186"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary uppercase"
                (input)="createVehicleForm.patchValue({vin: $any($event.target).value.toUpperCase()})"
              />
              @if (createVehicleForm.get('vin')?.invalid && createVehicleForm.get('vin')?.touched) {
                <p class="mt-1 text-sm text-red-600">VIN is required (17 characters)</p>
              }
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Make *</label>
                <input
                  type="text"
                  formControlName="make"
                  placeholder="Honda"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
                @if (createVehicleForm.get('make')?.invalid && createVehicleForm.get('make')?.touched) {
                  <p class="mt-1 text-sm text-red-600">Make is required</p>
                }
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                <input
                  type="text"
                  formControlName="model"
                  placeholder="Civic"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
                @if (createVehicleForm.get('model')?.invalid && createVehicleForm.get('model')?.touched) {
                  <p class="mt-1 text-sm text-red-600">Model is required</p>
                }
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                <input
                  type="number"
                  formControlName="year"
                  [min]="1900"
                  [max]="maxYear"
                  placeholder="2021"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
                @if (createVehicleForm.get('year')?.invalid && createVehicleForm.get('year')?.touched) {
                  <p class="mt-1 text-sm text-red-600">Valid year is required</p>
                }
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <input
                  type="text"
                  formControlName="color"
                  placeholder="Blue"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          <div class="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              (click)="closeCreateModal()"
              class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              [disabled]="submitting || createVehicleForm.invalid"
              class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              @if (submitting) {
                <i class="fas fa-spinner fa-spin mr-2"></i>
              }
              Add Vehicle
            </button>
          </div>
        </form>
      </div>
    </div>
  }
</div>

